import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./stage3.css";
import { fireGameAlert } from "./gameAlert";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  normalizePlayerName,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";
import { pledgeDisplayText } from "../thankyou/pledgeTexts";

import pledgeBg from "./img/pledgebg.png";
import title2Img from "./img/title2.png";
import tickImg from "./img/select.png";
import icon1 from "./img/icon1.png";
import icon2 from "./img/icon2.png";
import icon3 from "./img/icon3.png";
import icon4 from "./img/icon4.png";
import icon5 from "./img/icon5.png";
import icon6 from "./img/icon6.png";
import pledge1 from "./img/Pledge1.png";
import pledge2 from "./img/Pledge2.png";
import pledge3 from "./img/Pledge3.png";
import pledge4 from "./img/Pledge4.png";
import pledge5 from "./img/Pledge5.png";
import pledge6 from "./img/Pledge6.png";

const STAGE_NUM = 3;
const PLEDGE_POINTS = 10;
const CUSTOM_PLEDGE_INDEX = 5;

const PLEDGES = [
  { id: 1, icon: icon1, bubble: pledge1 },
  { id: 2, icon: icon2, bubble: pledge2 },
  { id: 3, icon: icon3, bubble: pledge3 },
  { id: 4, icon: icon4, bubble: pledge4 },
  { id: 5, icon: icon5, bubble: pledge5 },
  { id: 6, icon: icon6, bubble: pledge6, custom: true },
];

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Stage3() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [customText, setCustomText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };
    html.classList.add("stage3-lock");
    body.classList.remove("common-bg");
    body.classList.add("pledge-page");
    body.style.backgroundImage = `url("${pledgeBg}")`;
    body.style.backgroundSize = "100% 100%";
    body.style.backgroundRepeat = "no-repeat";
    body.style.backgroundPosition = "center center";
    body.style.backgroundAttachment = "fixed";
    return () => {
      html.classList.remove("stage3-lock");
      body.classList.remove("pledge-page");
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, []);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return undefined;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();
    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      elapsedRef.current = baseElapsedRef.current + sessionSec;
    }, 1000);
    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }
      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);
        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: normalizePlayerName(storedUser),
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }
        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));
        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }
          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage3 = Number(st.stage3) || 0;
          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage3);
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("Stage3 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  const finishStage = useCallback(async () => {
    if (submitting || submittedRef.current) return;
    if (selectedIndex == null) {
      fireGameAlert({ icon: "error", title: "Please select a pledge", text: " " });
      return;
    }
    if (selectedIndex === CUSTOM_PLEDGE_INDEX && !customText.trim()) {
      fireGameAlert({
        icon: "error",
        title: "Please write your pledge",
        text: " ",
      });
      return;
    }

    submittedRef.current = true;
    setSubmitting(true);

    const timeStr = formatSecondsToClock(elapsedRef.current);
    const stage3Score = PLEDGE_POINTS;
    const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
    const finalTotal = otherStagesTotalRef.current + stage3Score;
    const pledgeId = PLEDGES[selectedIndex].id;
    const ownPledge =
      selectedIndex === CUSTOM_PLEDGE_INDEX ? customText.trim() : "";
    const pledgeText = pledgeDisplayText(pledgeId, ownPledge);

    try {
      sessionStorage.setItem(
        "navratri_pledge",
        JSON.stringify({ pledgeId, ownPledge: pledgeText })
      );
    } catch {
      /* ignore */
    }

    if (!isDemoBypass && backendBase && adminToken && stageId) {
      await saveGameStageAndReport({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId,
        stageNumber: STAGE_NUM,
        score: stage3Score,
        time: timeStr,
        total_score: finalTotal,
        pledgename: String(pledgeId),
        ownpledgemsz: pledgeText,
      });
    }

    clearStageActive(STAGE_NUM);
    markStageCompleted(STAGE_NUM, storedUser);

    if (isDemoBypass) {
      await fireGameAlert({
        icon: "info",
        text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      window.location.assign("https://extramileplay.com/plans");
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 2000));
    navigate("/stage4");
  }, [
    submitting,
    selectedIndex,
    customText,
    storedUser,
    isDemoBypass,
    backendBase,
    adminToken,
    stageRowId,
    navigate,
  ]);

  if (!dbReady) {
    return <div className="stage3-loading">Loading game…</div>;
  }

  return (
    <div className="stage3-page">
      <div className="stage3-header">
        <img src={title2Img} alt="The Recipe Maze" className="stage3-title-img" />
        <p className="stage3-instructions">
          Identify innovative ways to reduce, reuse, or recycle materials and
          design your pledge in a visually appealing way
        </p>
      </div>

      <div className="stage3-grid">
        {PLEDGES.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className={`stage3-card${selectedIndex === index ? " is-selected" : ""}`}
            onClick={() => {
              setSelectedIndex(index);
              if (!item.custom) setCustomText("");
            }}
          >
            {selectedIndex === index ? (
              <img src={tickImg} alt="" className="stage3-tick" />
            ) : null}
            <img src={item.icon} alt="" className="stage3-icon" />
            <img src={item.bubble} alt="" className="stage3-bubble" />
          </button>
        ))}
      </div>

      {selectedIndex === CUSTOM_PLEDGE_INDEX ? (
        <div className="stage3-custom">
          <input
            className="stage3-custom-input"
            type="text"
            maxLength={100}
            placeholder="Write your pledge"
            value={customText}
            onChange={(e) => setCustomText(e.target.value.replace(/^\s+/, ""))}
          />
          <p className="stage3-custom-hint">Maximum 100 character</p>
        </div>
      ) : null}

      <div className="stage3-submit-wrap">
        <button
          type="button"
          className="stage3-submit"
          disabled={submitting}
          onClick={() => finishStage()}
        >
          Submit
        </button>
      </div>
    </div>
  );
}
